import {
  Bath,
  BedDouble,
  Building2,
  Camera,
  FileStack,
  Download,
  GraduationCap,
  House,
  MapPin,
  MessageCircle,
  PlayCircle,
  Ruler,
  ShoppingBag,
  TrainFront,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BrochureModal } from "@/components/BrochureModal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { ExitStrategyModeler } from "@/components/ExitStrategyModeler";
import { ForecastComparisonPanel } from "@/components/ForecastComparisonPanel";
import { ImageMoodToggle } from "@/components/ImageMoodToggle";
import { InvestmentForecastReportModal } from "@/components/InvestmentForecastReportModal";
import { InvestmentForecastWidget } from "@/components/InvestmentForecastWidget";
import { ListingReasoningPanel } from "@/components/ListingReasoningPanel";
import { ListingCompareSummaryBar } from "@/components/ListingCompareSummaryBar";
import { ListingRouteErrorState } from "@/components/ListingRouteErrorState";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { MarketIntelligenceWidget } from "@/components/market-intelligence/MarketIntelligenceWidget";
import { PropertyMoodImage } from "@/components/PropertyMoodImage";
import { ShortlistActionButton } from "@/components/agent-shortlist/ShortlistActionButton";
import { AgentTrustPanel } from "@/components/agent-trust/AgentTrustPanel";
import { AgentListingDetailSkeleton } from "@/components/agent/AgentPortalSkeletons";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SITE_LOGO_URL } from "@/data/portalConfig";
import { useSiteBranding } from "@/context/SiteContext";
import { useFastPolling } from "@/hooks/useFastPolling";
import { usePortalShortlist } from "@/hooks/usePortalShortlist";
import { useSeo } from "@/hooks/useSeo";
import { useStructuredData } from "@/hooks/useStructuredData";
import { getVirtualStagingPair, sanitizeMediaCollection } from "@/lib/listingMedia";
import { trackLeadIntent } from "@/lib/leadIntent";
import { getListingHeroPresentation } from "@/lib/listingVisualIntegrity";
import { api } from "@/lib/api";
import { formatCurrency, formatListingPrice, formatNumber } from "@/lib/portalHelpers";

export const ListingDetailPage = () => {
  const { listingId } = useParams();
  const { branding, buildBrandedPath, getWhatsAppHref, isAgentPath, leadFormPath, requestParams } = useSiteBranding();
  const shortlist = usePortalShortlist();
  const [listing, setListing] = useState(null);
  const [isListingLoading, setIsListingLoading] = useState(true);
  const [isListingNotFound, setIsListingNotFound] = useState(false);
  const [hasListingLoadError, setHasListingLoadError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openForecastModal, setOpenForecastModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedComparisons, setSelectedComparisons] = useState([]);
  const [imageMood, setImageMood] = useState("day");
  const [forecast, setForecast] = useState(null);
  const [forecastCompareCandidates, setForecastCompareCandidates] = useState([]);
  const [selectedForecastCompareIds, setSelectedForecastCompareIds] = useState([]);
  const [forecastCompareMap, setForecastCompareMap] = useState({});
  const [forecastCompareLoading, setForecastCompareLoading] = useState(false);
  const [premiumLead, setPremiumLead] = useState(null);

  useEffect(() => {
    try {
      const storedLead = window.localStorage.getItem("signature-advisor-captured-lead");
      if (storedLead) setPremiumLead(JSON.parse(storedLead));
    } catch {
      setPremiumLead(null);
    }
  }, []);

  const loadListing = useCallback(async (resetState = true) => {
    try {
      if (resetState) {
        setIsListingLoading(true);
        setListing(null);
        setIsListingNotFound(false);
        setHasListingLoadError(false);
        setSelectedComparisons([]);
        setLightboxImage(null);
        setOpenModal(false);
        setOpenForecastModal(false);
        setImageMood("day");
        setForecast(null);
        setForecastCompareCandidates([]);
        setSelectedForecastCompareIds([]);
        setForecastCompareMap({});
      }
      const listingResponse = await api.get(`/listings/${listingId}`, { params: requestParams });
      const nextListing = listingResponse.data;
      const canonicalListingId = nextListing?.id || listingId;
      setListing(nextListing);
      setIsListingNotFound(false);
      setHasListingLoadError(false);

      const forecastResponse = await api.get(`/listings/${canonicalListingId}/investment-forecast`, { params: requestParams }).catch(() => ({ data: null }));
      setForecast(forecastResponse.data);
    } catch (error) {
      setListing(null);
      setForecast(null);
      setForecastCompareCandidates([]);
      setSelectedForecastCompareIds([]);
      setForecastCompareMap({});
      setIsListingNotFound(error?.response?.status === 404);
      setHasListingLoadError(error?.response?.status !== 404);
    } finally {
      setIsListingLoading(false);
    }
  }, [listingId, requestParams]);

  useSeo({
    title: listing ? `${listing.title} | SG Prop Portal` : "Listing Detail | SG Prop Portal",
    description: listing ? listing.description : "Explore a premium project showcase with floor plans, nearby amenities, and direct team contact.",
  });

  const listingStructuredData = useMemo(() => {
    if (!listing || isAgentPath) return null;
    const pageUrl = `https://sgpropportal.com/listings/${listing?.id || listingId}`;
    const images = [listing.image_url, ...(listing.gallery_urls || [])].filter(Boolean).slice(0, 8);
    return {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.title,
      description: listing.description,
      url: pageUrl,
      image: images,
      datePosted: listing.created_at || listing.updated_at,
      offers: {
        "@type": "Offer",
        price: listing.price,
        priceCurrency: "SGD",
        availability: String(listing.status || "available").toLowerCase().includes("sold") ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
        url: pageUrl,
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: listing.address || listing.title,
        addressLocality: "Singapore",
        addressCountry: "SG",
      },
      floorSize: listing.sqft
        ? {
            "@type": "QuantitativeValue",
            value: listing.sqft,
            unitCode: "FTK",
          }
        : undefined,
      numberOfRooms: listing.bedrooms || undefined,
      numberOfBathroomsTotal: listing.bathrooms || undefined,
      seller: {
        "@type": "Organization",
        name: "SG Prop Portal",
      },
    };
  }, [isAgentPath, listing, listingId]);

  const listingBreadcrumbSchema = useMemo(() => {
    if (!listing || isAgentPath) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://sgpropportal.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Listings",
          item: "https://sgpropportal.com/listings",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: listing.title,
          item: `https://sgpropportal.com/listings/${listing?.id || listingId}`,
        },
      ],
    };
  }, [isAgentPath, listing, listingId]);

  useStructuredData("listing-detail-schema", listingStructuredData);
  useStructuredData("listing-detail-breadcrumb", listingBreadcrumbSchema);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  useEffect(() => {
    if (!listing || !premiumLead?.email) return;
    trackLeadIntent({ premiumLead, eventType: "listing_view", listing });
  }, [listing?.id, premiumLead?.email]);

  useEffect(() => {
    if (!listing?.district) {
      setForecastCompareCandidates([]);
      return;
    }
    let cancelled = false;
    const loadForecastComparables = async () => {
      try {
        const response = await api.get("/listings", { params: { district: listing.district, limit: 8, ...requestParams } });
        if (cancelled) return;
        const candidates = (response.data || []).filter((item) => item.id !== listing.id).slice(0, 6);
        setForecastCompareCandidates(candidates);
      } catch {
        if (!cancelled) setForecastCompareCandidates([]);
      }
    };
    loadForecastComparables();
    return () => {
      cancelled = true;
    };
  }, [listing?.district, listing?.id, requestParams]);

  useEffect(() => {
    if (!selectedForecastCompareIds.length) {
      setForecastCompareMap({});
      return;
    }
    let cancelled = false;
    const loadComparedForecasts = async () => {
      setForecastCompareLoading(true);
      try {
        const responses = await Promise.all(
          selectedForecastCompareIds.map((compareId) => api.get(`/listings/${compareId}/investment-forecast`, { params: requestParams }).catch(() => ({ data: null }))),
        );
        if (cancelled) return;
        const nextMap = {};
        selectedForecastCompareIds.forEach((compareId, index) => {
          nextMap[compareId] = responses[index]?.data || null;
        });
        setForecastCompareMap(nextMap);
      } finally {
        if (!cancelled) setForecastCompareLoading(false);
      }
    };
    loadComparedForecasts();
    return () => {
      cancelled = true;
    };
  }, [requestParams, selectedForecastCompareIds]);

  const toggleForecastComparison = (compareId) => {
    setSelectedForecastCompareIds((current) => {
      if (current.includes(compareId)) return current.filter((item) => item !== compareId);
      if (current.length >= 2) return [...current.slice(1), compareId];
      return [...current, compareId];
    });
  };

  useFastPolling(() => loadListing(false), 12000, Boolean(listingId) && !isListingNotFound);

  if (isListingLoading && !listing) {
    if (isAgentPath) {
      return <AgentListingDetailSkeleton />;
    }
    return (
      <section className="section-shell">
        <div className="rounded-[28px] border border-[#001935]/10 bg-white/80 p-8 text-[#333333]" data-testid="listing-detail-loading-state">
          Loading listing details...
        </div>
      </section>
    );
  }

  if (isListingNotFound) {
    return (
      <section className="section-shell py-12" data-testid="listing-not-found-state">
        <div className="mx-auto max-w-4xl rounded-[32px] border border-[#001935]/10 bg-white/90 p-8 shadow-[0_24px_70px_rgba(0,25,53,0.08)] md:p-10">
          <div className="inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#fff7e6] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="listing-not-found-badge">
            Listing unavailable
          </div>
          <h1 className="mt-5 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935] sm:text-5xl" data-testid="listing-not-found-heading">
            This listing could not be found.
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-8 text-[#42566f] sm:text-base" data-testid="listing-not-found-copy">
            The link may be invalid, unpublished, or no longer available on this portal. You can return to the listings collection and continue browsing safely.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="listing-not-found-actions">
            <Link className="gold-button !px-5 !py-3" data-testid="listing-not-found-listings-link" to={buildBrandedPath("/listings")}>
              View Listings
            </Link>
            <Link className="outline-button" data-testid="listing-not-found-home-link" to={buildBrandedPath("/")}>
              Back Home
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (hasListingLoadError || !listing) {
    return <ListingRouteErrorState homePath={buildBrandedPath("/")} listingsPath={buildBrandedPath("/listings")} />;
  }

  const showcase = listing.project_showcase || {};
  const rawGalleryImages = showcase.gallery?.length ? showcase.gallery : listing.gallery_urls;
  const galleryImages = sanitizeMediaCollection(rawGalleryImages);
  const heroPresentation = getListingHeroPresentation(listing, galleryImages);
  const heroImage = heroPresentation.heroImage;
  const virtualStagingPair = getVirtualStagingPair({
    beforeImage: listing.staging_before_url,
    afterImage: listing.staging_after_url,
  });
  const floorplanImages = showcase.floorplans?.length ? showcase.floorplans : listing.floorplan_urls;
  const floorplanPdfUrls = showcase.floorplan_pdfs?.length ? showcase.floorplan_pdfs : listing.floorplan_pdf_urls || [];
  const siteplanImages = listing.siteplan_urls || [];
  const siteplanPdfUrls = listing.siteplan_pdf_urls || [];
  const overviewText = showcase.overview || listing.description;
  const propertyDetails = (() => {
    const fallbackDetails = [
      { label: "Tenure", value: listing.tenure },
      { label: "District", value: listing.district },
      { label: "Property Type", value: listing.property_type },
      { label: "Price", value: formatListingPrice(listing) },
      { label: "Size", value: `${formatNumber(listing.sqft)} sqft` },
      { label: "Status", value: listing.status },
    ];

    if (!showcase.property_details?.length) return fallbackDetails;

    const details = [...showcase.property_details];
    const hasPriceRow = details.some((detail) => String(detail.label || "").toLowerCase().includes("price"));
    if (!hasPriceRow) {
      details.unshift({ label: "Price", value: formatListingPrice(listing) });
    }
    return details;
  })();
  const unitMix = showcase.unit_mix?.length
    ? showcase.unit_mix
    : [{ type: `${listing.bedrooms || "-"} Bedroom`, size: `${formatNumber(listing.sqft)}`, price: formatListingPrice(listing) }];
  const nearbyGroups = [
    { key: "mrt", label: "MRT", icon: TrainFront },
    { key: "schools", label: "Schools", icon: GraduationCap },
    { key: "shopping", label: "Shopping", icon: ShoppingBag },
  ].filter((group) => showcase.nearby?.[group.key]?.length);
  const faqItems = showcase.faq_items?.length
    ? showcase.faq_items
    : [
        { question: `What makes ${listing.title} worth viewing?`, answer: listing.description },
        { question: "How can I arrange a viewing?", answer: "Use the SG Prop Portal contact section to WhatsApp the team directly for the fastest response." },
      ];
  const tourCards = showcase.tour_cards?.length
    ? showcase.tour_cards
    : listing.tour_embed_url
      ? [{ title: "Immersive 360° View", description: "Open the available tour media for a deeper walkthrough.", url: listing.tour_embed_url }]
      : [];
  const towerViewItems = showcase.tower_view_items?.length
    ? showcase.tower_view_items
    : floorplanImages.map((image, index) => ({ label: `Plan ${String(index + 1).padStart(2, "0")}`, image_url: image }));
  const floorplanDocuments = floorplanPdfUrls.map((url, index) => ({
    url,
    label: `PDF Plan ${String(index + 1).padStart(2, "0")}`,
  }));
  const siteplanDocuments = siteplanPdfUrls.map((url, index) => ({
    url,
    label: `PDF Site Plan ${String(index + 1).padStart(2, "0")}`,
  }));
  const sectionLinks = [
    { label: "Overview", href: "#overview" },
    { label: "Forecast", href: "#investment-forecast" },
    { label: "Staging", href: "#virtual-staging" },
    { label: "Details", href: "#details" },
    { label: "Nearby", href: "#location" },
    { label: "Floor Plans", href: "#floor-plans" },
    { label: "Gallery", href: "#gallery" },
    { label: "FAQ", href: "#faq" },
  ];

  const toggleComparison = (index) => {
    setSelectedComparisons((current) => {
      if (current.includes(index)) {
        return current.filter((item) => item !== index);
      }
      if (current.length >= 2) {
        return [current[1], index];
      }
      return [...current, index];
    });
  };

  const reviewComparison = () => {
    const target = document.querySelector('[data-testid="listing-detail-compare-panel"]') || document.querySelector('[data-testid="listing-detail-unit-mix-section"]');
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleFindSimilarUnits = () => {
    window.dispatchEvent(
      new CustomEvent("signature-advisor-open", {
        detail: {
          listing: {
            id: listing.id,
            title: listing.title,
            district: listing.district,
            property_type: listing.property_type,
            category: listing.category,
            price: listing.price,
            display_price: listing.display_price,
            sqft: listing.sqft,
            psf: listing.psf,
            status: listing.status,
            image_url: listing.image_url,
          },
        },
      }),
    );
  };

  const comparisonUnits = selectedComparisons.map((index) => ({ ...unitMix[index], index })).filter(Boolean);
  const sectionEyebrowClass = `text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37] ${isAgentPath ? "agent-portal-kicker" : ""}`.trim();
  const alphaScore = forecast
    ? Math.min(
        99,
        ((Number(forecast.estimated_rental_yield_pct || 0) * 12.5)
          + (Math.max(Number(forecast.value_gap_percentage || 0), 0) * 1.9)
          + (Number(forecast.five_year_appreciation_pct || 0) * 0.62)),
      ).toFixed(1)
    : null;
  const listingWhatsAppHref = getWhatsAppHref(`Hello, I would like to enquire about ${listing.title}.`);
  const buildShortlistPath = buildBrandedPath(`/listings?shortlist=${Array.from(new Set([listing.id, ...shortlist.ids])).join(",")}`);
  const floorLevel = showcase.floor_level || listing.floor_level || listing.floor || "—";
  const advisorFirstName = String((branding?.name || "Advisor").split(" ")[0] || "Advisor");
  const heroEyebrow = isAgentPath ? "Property Snapshot" : (heroPresentation.usingCuratedFallback ? "Signature Editorial Backdrop" : "Editorial Signature Detail");
  const stickySectionLinks = isAgentPath
    ? [
        { label: "Overview", href: "#overview" },
        { label: "Price Outlook", href: "#investment-forecast" },
        { label: "Details", href: "#details" },
        { label: "Nearby", href: "#location" },
        { label: "Floor Plans", href: "#floor-plans" },
        { label: "Gallery", href: "#gallery" },
        { label: "FAQ", href: "#faq" },
      ]
    : sectionLinks;
  const editorialEyebrow = isAgentPath ? "Property Brief" : "Signature Alpha Editorial";
  const editorialHeading = isAgentPath ? "Everything you need before you arrange a viewing." : listing.title;
  const editorialCopy = isAgentPath
    ? `A cleaner property brief with pricing context, nearby essentials, and next-step tools so you can move quickly with ${advisorFirstName}.`
    : "A Signature Editorial briefing with live Alpha Formula intelligence, premium project detail, and investor-grade next-step tools.";
  const alphaBadgeLabel = isAgentPath ? `Market Score ${alphaScore}` : `Alpha Score ${alphaScore}`;
  const downloadBriefLabel = isAgentPath ? "Download Property Brief" : "Download Alpha Forecast Memo";
  const downloadBriefNote = isAgentPath
    ? `${advisorFirstName} can follow up with a clearer explanation, nearby alternatives, or a viewing plan after you download the brief.`
    : "Lead capture remains active before download so Clive can follow up with a bespoke Alpha interpretation for this listing.";
  const sidebarEyebrow = isAgentPath ? "Talk to your advisor" : "Direct advisor access";
  const sidebarHeading = isAgentPath ? `Need a walkthrough of ${listing.title}?` : "Need a guided walkthrough?";
  const sidebarCopy = isAgentPath
    ? `Speak with ${branding.name} for pricing clarity, nearby alternatives, or the fastest viewing arrangement.`
    : `Speak with ${branding.name} for pricing clarity, unit comparison, or the fastest viewing arrangement.`;
  const whatsappButtonLabel = isAgentPath ? `WhatsApp ${advisorFirstName}` : "WhatsApp for Insights";
  const projectPackLabel = isAgentPath ? "Download Project Pack" : "Project Pack";
  const mobileContactLinkLabel = isAgentPath ? `Contact ${advisorFirstName}` : "Contact Us";
  const quickStats = [
    { label: "Beds", value: listing.bedrooms || "—", icon: BedDouble },
    { label: "Baths", value: listing.bathrooms || "—", icon: Bath },
    { label: "SQFT", value: listing.sqft ? formatNumber(listing.sqft) : "—", icon: Ruler },
    { label: "Floor Level", value: floorLevel, icon: Building2 },
  ];
  const nearbyAmenityCount = nearbyGroups.reduce((total, group) => total + (showcase.nearby?.[group.key]?.length || 0), 0);

  const copyListingLink = async () => {
    if (!navigator?.clipboard) {
      toast.error("Clipboard access is unavailable right now.");
      return;
    }
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Listing link copied");
  };

  return (
    <div className={`bg-[#F9F8F6] pb-24 text-[#2C2C2C] md:pb-28 ${isAgentPath ? "agent-portal-page-stack" : ""}`.trim()}>
      {heroPresentation.heroMode === "cream-fallback" ? (
        <section className="relative min-h-[42vh] overflow-hidden bg-[#F9F8F6]" data-testid="listing-detail-showcase-hero-fallback">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_12%,rgba(212,175,55,0.18),transparent_28%),linear-gradient(180deg,rgba(249,248,246,0.94)_0%,rgba(249,248,246,1)_100%)]" />
          <div className="relative mx-auto flex min-h-[56vh] max-w-5xl items-center justify-center px-6 py-20 text-center md:px-10 lg:px-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Project Showcase</p>
              <h1 className={`mt-6 font-[Cormorant_Garamond,serif] text-5xl font-bold tracking-[0.04em] text-[#2C2C2C] md:text-7xl ${isAgentPath ? "agent-portal-heading-xl" : ""}`.trim()} data-testid="listing-detail-title">
                {listing.title}
              </h1>
              <p className="mt-6 text-base leading-8 text-[#5D584F] md:text-lg" data-testid="listing-detail-address">{listing.address}</p>
              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/35 bg-white px-5 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#8B6E3D]" data-testid="listing-detail-district-pill">
                <MapPin className="h-4 w-4" /> {listing.district}
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section className="relative min-h-[54vh] overflow-hidden" data-testid={heroPresentation.usingCuratedFallback ? "listing-detail-showcase-hero-curated-fallback" : "listing-detail-showcase-hero"}>
          <img alt={heroPresentation.fallbackAlt || listing.title} className={`absolute inset-0 h-full w-full object-cover ${heroPresentation.usingCuratedFallback ? "object-center" : "object-[center_34%]"}`} data-testid="listing-detail-hero-image" decoding="async" loading="lazy" src={heroImage} />
          <div className={`absolute inset-0 ${heroPresentation.usingCuratedFallback ? "bg-[linear-gradient(180deg,rgba(22,21,19,0.18)_0%,rgba(22,21,19,0.26)_28%,rgba(249,248,246,0.82)_76%,rgba(249,248,246,0.96)_100%)]" : "bg-[linear-gradient(180deg,rgba(249,248,246,0.08)_0%,rgba(249,248,246,0.22)_36%,rgba(249,248,246,0.88)_100%)]"}`} />
          <div className="relative mx-auto flex min-h-[54vh] max-w-7xl items-end px-6 pb-10 pt-24 md:px-10 lg:px-12">
            <div className="max-w-4xl text-white">
              <div className="rounded-[30px] border border-white/70 bg-[#F9F8F6]/88 p-6 text-[#2C2C2C] shadow-[0_24px_80px_rgba(44,44,44,0.12)] backdrop-blur-xl md:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#8B6E3D]" data-testid="listing-detail-hero-eyebrow">{heroEyebrow}</p>
                <h1 className={`mt-4 font-[Cormorant_Garamond,serif] text-5xl font-bold md:text-7xl ${isAgentPath ? "agent-portal-heading-xl" : ""}`.trim()} data-testid="listing-detail-title">
                  {listing.title}
                </h1>
                <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-[#5D584F]">
                  <span className="inline-flex items-center gap-2" data-testid="listing-detail-address"><MapPin className="h-4 w-4 text-[#D4AF37]" /> {listing.address}</span>
                  <span className="rounded-full border border-[#D4AF37]/35 bg-white px-4 py-2 text-[#2C2C2C]">{listing.district}</span>
                </div>
                {heroPresentation.usingCuratedFallback ? (
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-[#5D584F]" data-testid="listing-detail-curated-hero-note">
                    This listing is using a curated editorial backdrop because the live source imagery is map-, floor-plan-, or promo-led. All live project data, nearby insights, and gallery media remain preserved below.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="sticky top-[84px] z-30 border-y border-[#D4AF37]/20 bg-[#F9F8F6]/92 backdrop-blur" data-testid="listing-detail-sticky-menu" style={{ position: "sticky", top: "84px" }}>
        <div className="mx-auto flex max-w-7xl gap-3 overflow-x-auto px-6 py-4 md:px-10 lg:px-12">
          {stickySectionLinks.map((item) => (
            <a className="whitespace-nowrap rounded-full border border-[#2C2C2C]/10 bg-white px-4 py-2 text-sm font-semibold text-[#5D584F] transition hover:border-[#D4AF37]/40 hover:text-[#8B6E3D]" data-testid={`listing-detail-menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`} href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <section className="section-shell pb-0 pt-10" data-testid="listing-detail-editorial-header-section">
        <div className="signature-greige-shell grid gap-8 rounded-[34px] p-6 md:p-8 xl:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8B6E3D]">{editorialEyebrow}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <h2 className={`font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#2C2C2C] md:text-6xl ${isAgentPath ? "agent-portal-heading-lg" : ""}`.trim()} data-testid="listing-detail-editorial-heading">{editorialHeading}</h2>
              {alphaScore ? (
                <div className="inline-flex items-center rounded-full border border-[#D4AF37]/40 bg-[#2C2C2C] px-4 py-2 text-sm font-bold uppercase tracking-[0.18em] text-[#D4AF37]" data-testid="listing-detail-alpha-score-badge">
                  {alphaBadgeLabel}
                </div>
              ) : null}
            </div>
            <p className={`mt-4 max-w-3xl text-base leading-8 text-[#5D584F] ${isAgentPath ? "agent-portal-body-copy" : ""}`.trim()}>{editorialCopy}</p>
          </div>

          <div className="flex flex-col items-start gap-4 xl:items-end">
            <button className="gold-button !px-6 !py-4 !text-sm !tracking-[0.18em]" data-testid="listing-detail-download-alpha-memo-button" onClick={() => setOpenForecastModal(true)} type="button">
              <Download className="mr-2 h-4 w-4" /> {downloadBriefLabel}
            </button>
            {isAgentPath ? <div className="flex flex-wrap gap-2 xl:justify-end">
              <ShortlistActionButton listing={listing} testId="listing-detail-header-shortlist-button" variant="soft" />
              <button className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="listing-detail-header-copy-link-button" onClick={copyListingLink} type="button">
                Copy link
              </button>
              <Link className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid="listing-detail-header-open-shortlist-link" to={buildShortlistPath}>
                Open shortlist
              </Link>
            </div> : null}
            <p className="text-sm leading-7 text-[#5D584F] xl:max-w-sm xl:text-right">{downloadBriefNote}</p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="listing-detail-editorial-stats-grid">
          {quickStats.map((item) => {
            const Icon = item.icon;
            return (
              <div className="signature-greige-shell rounded-[28px] px-5 py-5" data-testid={`listing-detail-hero-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                <div className="flex items-center gap-3 text-[#D4AF37]"><Icon className="h-5 w-5" /><p className="text-xs font-bold uppercase tracking-[0.18em] text-[#2C2C2C]">{item.label}</p></div>
                <p className="mt-4 text-3xl font-semibold text-[#2C2C2C]">{item.value}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section-shell pt-8" data-testid="listing-detail-market-intelligence-section">
        <MarketIntelligenceWidget
          autoLoad
          compact
          defaultDistrictCode={listing.district}
          subheading={isAgentPath ? `Keep visitors engaged on ${branding.name}'s portal with a district-level transaction read before they move into forecast and viewing tools.` : "Read the latest district-level caveat activity for this listing before moving into the rest of the investment tools."}
          testIdPrefix="listing-detail-market-intelligence"
          title={`${listing.district || "District"} transaction intelligence`}
        />
      </section>

      <section className="section-shell pt-10">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-8">
            {forecast ? (
              <InvestmentForecastWidget forecast={forecast} isAgentTheme={isAgentPath} onUnlockReport={() => setOpenForecastModal(true)} />
            ) : (
              <section className="signature-greige-shell rounded-[30px] p-6 shadow-[0_22px_70px_rgba(0,0,0,0.06)] md:p-8" id="investment-forecast" data-testid="listing-detail-investment-forecast-fallback-section">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B6E3D]">Price Outlook</p>
                <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#2C2C2C]">A custom pricing brief can still be prepared for this home.</h2>
                <p className="mt-4 max-w-3xl text-sm leading-8 text-[#5D584F]">
                  Live forecast data is not available for this listing yet, usually because the source record is missing complete pricing inputs. You can still request the project pack or WhatsApp {advisorFirstName} for a direct assessment and nearby alternatives.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a className="gold-button" data-testid="listing-detail-investment-forecast-fallback-whatsapp" href={listingWhatsAppHref} rel="noreferrer" target="_blank">
                    <MessageCircle className="mr-2 h-4 w-4" /> {whatsappButtonLabel}
                  </a>
                  <button className="outline-button" data-testid="listing-detail-investment-forecast-fallback-brochure" onClick={() => setOpenModal(true)} type="button">
                    <Download className="mr-2 h-4 w-4" /> {projectPackLabel}
                  </button>
                </div>
              </section>
            )}

            <ListingReasoningPanel buildShortlistPath={buildShortlistPath} forecast={forecast} isAgentTheme={isAgentPath} listing={listing} onCopyListingLink={copyListingLink} showShortlistActions={isAgentPath} />

            <section className="signature-greige-shell scroll-mt-36 rounded-[30px] p-6 shadow-[0_22px_70px_rgba(0,0,0,0.06)] md:p-8" id="overview" data-testid="listing-detail-overview-section">
              <p className={sectionEyebrowClass}>Overview</p>
              <p className="mt-4 text-base leading-8 text-[#3F3A32]">{overviewText}</p>
              {listing.source_note ? <p className="mt-6 text-[11px] leading-6 text-[#7B746A]">{listing.source_note}</p> : null}
            </section>

            {forecast ? (
              <ForecastComparisonPanel
                comparedForecasts={forecastCompareMap}
                currentForecast={forecast}
                isAgentTheme={isAgentPath}
                currentListing={listing}
                loading={forecastCompareLoading}
                onToggle={toggleForecastComparison}
                selectedIds={selectedForecastCompareIds}
                selectableListings={forecastCompareCandidates}
              />
            ) : null}

            {forecast ? <ExitStrategyModeler forecast={forecast} isAgentTheme={isAgentPath} listing={listing} onRequirePremium={() => setOpenForecastModal(true)} premiumLead={premiumLead} /> : null}

            <section className="scroll-mt-36" id="virtual-staging" data-testid="listing-detail-virtual-staging-section">
              {virtualStagingPair.beforeImage && virtualStagingPair.afterImage ? (
                <BeforeAfterSlider afterImage={virtualStagingPair.afterImage} beforeImage={virtualStagingPair.beforeImage} title="Signature Virtual Staging" />
              ) : (
                <div className="signature-greige-shell rounded-[30px] border border-dashed border-[#D4AF37]/35 p-8 text-center shadow-[0_18px_60px_rgba(0,25,53,0.05)]" data-testid="listing-detail-virtual-staging-placeholder">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">AI Virtual Staging (Beta)</p>
                  <h3 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#2C2C2C]">Coming Soon</h3>
                  <p className="mt-4 text-sm leading-8 text-[#5D584F]">This listing does not yet have both staging reference images saved. Once our team uploads the before and after URLs, the interactive slider will appear here automatically.</p>
                </div>
              )}
            </section>

            <section className="agent-paper-section signature-greige-shell scroll-mt-36 rounded-[30px] p-6 shadow-[0_22px_70px_rgba(0,0,0,0.06)] md:p-8" id="details" data-testid="listing-detail-details-section">
              <p className={sectionEyebrowClass}>Project Details</p>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {propertyDetails.map((detail) => (
                  <div className="overflow-hidden rounded-[24px] border border-[#2C2C2C]/10 bg-white" data-testid={`listing-detail-property-row-${detail.label.toLowerCase().replace(/\s+/g, "-")}`} key={detail.label}>
                    <div className="bg-[#F1E5D1] px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#8B6E3D]">{detail.label}</div>
                    <div className="px-4 py-4 text-sm leading-7 text-[#2C2C2C]">{detail.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-center md:justify-start">
                <button
                  className="inline-flex w-full max-w-md items-center justify-center rounded-full border border-[#D4AF37] bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-[#121212] shadow-[0_10px_30px_rgba(0,25,53,0.06)] transition hover:-translate-y-0.5 hover:bg-[#e1bb4f] hover:shadow-[0_18px_40px_rgba(212,175,55,0.16)] md:w-auto"
                  data-testid="listing-detail-find-similar-button"
                  onClick={handleFindSimilarUnits}
                  type="button"
                >
                  🔍 Find Similar Units in {listing.district}
                </button>
              </div>
            </section>

            {unitMix.length ? (
              <section className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-unit-mix-section">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className={sectionEyebrowClass}>Bedroom Types</p>
                    <p className="mt-2 text-sm leading-7 text-[#5D584F]">Select up to two units to compare their size, pricing, and corresponding floor-plan preview side by side.</p>
                  </div>
                  {comparisonUnits.length ? (
                    <button className="outline-button" data-testid="listing-detail-compare-clear-button" onClick={() => setSelectedComparisons([])} type="button">
                      Clear Compare
                    </button>
                  ) : null}
                </div>
                <div className="mt-6 overflow-hidden rounded-[24px] border border-[#D4AF37]/35">
                  <div className="grid grid-cols-[1.1fr_0.8fr_0.9fr_0.7fr] bg-[#F1E5D1] px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#8B6E3D]">
                    <span>Unit Type</span>
                    <span>Size (Sqft)</span>
                    <span>Price Range</span>
                    <span>Compare</span>
                  </div>
                  {unitMix.map((row, index) => (
                    <div className="grid grid-cols-[1.1fr_0.8fr_0.9fr_0.7fr] items-center gap-4 border-t border-[#D4AF37]/20 bg-white px-4 py-4 text-sm text-[#2C2C2C]" data-testid={`listing-detail-unit-mix-row-${index + 1}`} key={`${row.type}-${index}`}>
                      <span>{row.type}</span>
                      <span>{row.size}</span>
                      <span>{row.price}</span>
                      <button className={`rounded-full border px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${selectedComparisons.includes(index) ? "border-[#D4AF37] bg-[#D4AF37] text-[#121212]" : "border-[#D4AF37]/30 text-[#8B6E3D] hover:bg-[#D4AF37]/10"}`} data-testid={`listing-detail-unit-compare-button-${index + 1}`} onClick={() => toggleComparison(index)} type="button">
                        {selectedComparisons.includes(index) ? "Selected" : "Compare"}
                      </button>
                    </div>
                  ))}
                </div>

                {comparisonUnits.length ? (
                  <div className="mt-6 grid gap-5 lg:grid-cols-2" data-testid="listing-detail-compare-panel">
                    {comparisonUnits.map((unit) => (
                      <div className="signature-greige-soft rounded-[26px] border border-[#D4AF37]/30 p-5 shadow-[0_12px_40px_rgba(0,25,53,0.05)]" data-testid={`listing-detail-compare-card-${unit.index + 1}`} key={`${unit.type}-${unit.index}`}>
                        <p className="subtle-label">Comparison Pick</p>
                        <h3 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">{unit.type}</h3>
                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">Size</p>
                            <p className="mt-2 text-lg font-semibold text-[#2C2C2C]">{unit.size} sqft</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">Price</p>
                            <p className="mt-2 text-lg font-semibold text-[#2C2C2C]">{unit.price}</p>
                          </div>
                        </div>
                        {floorplanImages[unit.index] ? (
                          <button className="mt-5 overflow-hidden rounded-[22px] border border-[#D4AF37]/20 bg-white p-3" data-testid={`listing-detail-compare-floorplan-${unit.index + 1}`} onClick={() => setLightboxImage({ src: floorplanImages[unit.index], alt: `${listing.title} ${unit.type} floor plan`, variant: "plan" })} type="button">
                            <img alt={`${listing.title} ${unit.type} floor plan`} className="aspect-[1/1.1] w-full rounded-2xl object-cover" decoding="async" loading="lazy" src={floorplanImages[unit.index]} />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            <section className="agent-paper-section signature-greige-shell scroll-mt-36 rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" id="location" data-testid="listing-detail-nearby-section">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="subtle-label">Amenities & Nearby Insights</p>
                  <h3 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]" data-testid="listing-detail-nearby-heading">Everything around the address, preserved.</h3>
                  <p className="mt-3 max-w-3xl text-sm leading-8 text-[#5D584F]" data-testid="listing-detail-nearby-summary">
                    {nearbyGroups.length
                      ? `${nearbyAmenityCount} live nearby anchors are still surfaced here across transport, schools, and shopping so the editorial redesign keeps the practical decision-making layer fully intact.`
                      : `Structured nearby intelligence is not available for this listing yet, but the page keeps this section visible so the editorial layout stays consistent while the live data pack is refreshed.`}
                  </p>
                </div>
                <div className="rounded-full border border-[#2C2C2C]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8B6E3D]" data-testid="listing-detail-nearby-count-pill">
                  {nearbyAmenityCount} nearby data points
                </div>
              </div>
              {nearbyGroups.length ? (
                <div className="mt-6 grid gap-5 lg:grid-cols-3">
                  {nearbyGroups.map((group) => {
                    const Icon = group.icon;
                    return (
                      <div className="signature-greige-soft rounded-[26px] border border-[#D4AF37]/25 p-5" data-testid={`listing-detail-nearby-${group.key}`} key={group.key}>
                        <div className="flex items-center gap-3 text-[#8b6d16]"><Icon className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-[0.18em]">{group.label}</p></div>
                        <div className="mt-5 space-y-4">
                          {showcase.nearby[group.key].map((item) => (
                            <div key={`${group.key}-${item.name}`}>
                              <p className="font-semibold text-[#2C2C2C]">{item.name}</p>
                              <p className="text-sm text-[#5D584F]">{item.distance}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-6 signature-greige-soft rounded-[26px] border border-dashed border-[#D4AF37]/30 p-6" data-testid="listing-detail-nearby-placeholder">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Nearby intelligence refresh pending</p>
                  <p className="mt-3 text-sm leading-8 text-[#5D584F]">Use the listing address above for immediate context while we continue syncing transport, schools, and shopping data for this route.</p>
                </div>
              )}
            </section>

            {tourCards.length ? (
              <section className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-tour-cards-section">
                <p className="subtle-label">Virtual Tours & Quick Access</p>
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  {tourCards.map((card) => (
                    <a className="group signature-greige-soft rounded-[28px] border border-[#D4AF37]/30 p-5 transition hover:-translate-y-1 hover:border-[#D4AF37]/70" data-testid={`listing-detail-tour-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`} href={card.url} key={card.title} rel="noreferrer" target="_blank">
                      <div className="flex items-center gap-3 text-[#8b6d16]"><PlayCircle className="h-5 w-5" /><p className="text-sm font-semibold uppercase tracking-[0.18em]">Quick Access</p></div>
                      <h3 className="mt-4 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">{card.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-[#5D584F]">{card.description}</p>
                    </a>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="scroll-mt-36 space-y-8" id="floor-plans" data-testid="listing-detail-floorplan-section">
              {floorplanImages.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8">
                  <p className="subtle-label">Floor Plans</p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {floorplanImages.map((plan, index) => (
                      <button className="overflow-hidden rounded-[26px] border border-[#D4AF37]/25 bg-white p-3 transition hover:border-[#D4AF37]/70" data-testid={`listing-detail-floorplan-card-${index + 1}`} key={plan} onClick={() => setLightboxImage({ src: plan, alt: `${listing.title} floor plan ${index + 1}`, variant: "plan" })} type="button">
                        <img alt={`${listing.title} floor plan ${index + 1}`} className="aspect-[1/1.15] w-full rounded-2xl object-cover" decoding="async" loading="lazy" src={plan} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {siteplanImages.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-siteplan-image-section">
                  <p className="subtle-label">Site Plans</p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {siteplanImages.map((plan, index) => (
                      <button className="overflow-hidden rounded-[26px] border border-[#D4AF37]/25 bg-white p-3 transition hover:border-[#D4AF37]/70" data-testid={`listing-detail-siteplan-card-${index + 1}`} key={plan} onClick={() => setLightboxImage({ src: plan, alt: `${listing.title} site plan ${index + 1}`, variant: "plan" })} type="button">
                        <img alt={`${listing.title} site plan ${index + 1}`} className="aspect-[1/1.15] w-full rounded-2xl object-cover" decoding="async" loading="lazy" src={plan} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {floorplanDocuments.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-floorplan-pdf-section">
                  <p className="subtle-label">Uploaded Site Plans & PDF Floor Plans</p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {floorplanDocuments.map((document) => (
                      <article className="signature-greige-soft rounded-[26px] border border-[#D4AF37]/25 bg-white p-5" data-testid={`listing-detail-floorplan-pdf-card-${document.label.toLowerCase().replace(/\s+/g, "-")}`} key={document.url}>
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#D4AF37]/25 bg-[#fffaf1] text-[#8b6d16]">
                            <FileStack className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">PDF plan document</p>
                            <h3 className="mt-2 font-[Cormorant_Garamond,serif] text-2xl font-bold text-[#2C2C2C]">{document.label}</h3>
                            <p className="mt-3 text-sm leading-7 text-[#5D584F]">Open the uploaded PDF to review the project site plan, stack layout, or additional floor plan pages.</p>
                          </div>
                        </div>
                        <div className="mt-5 overflow-hidden rounded-[20px] border border-[#D4AF37]/20 bg-[#fffaf1]" data-testid={`listing-detail-floorplan-pdf-preview-${document.label.toLowerCase().replace(/\s+/g, "-")}`}>
                          <iframe className="h-[360px] w-full bg-white" src={`${document.url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`} title={`${document.label} preview`} />
                        </div>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <a className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16] transition hover:border-[#D4AF37]/70 hover:bg-[#f7ecd1]" data-testid={`listing-detail-floorplan-pdf-open-link-${document.label.toLowerCase().replace(/\s+/g, "-")}`} href={document.url} rel="noreferrer" target="_blank">
                            Open PDF <Download className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}

              {siteplanDocuments.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-siteplan-pdf-section">
                  <p className="subtle-label">Uploaded Site Plan PDFs</p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {siteplanDocuments.map((document) => (
                      <article className="signature-greige-soft rounded-[26px] border border-[#D4AF37]/25 bg-white p-5" data-testid={`listing-detail-siteplan-pdf-card-${document.label.toLowerCase().replace(/\s+/g, "-")}`} key={document.url}>
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#D4AF37]/25 bg-[#fffaf1] text-[#8b6d16]">
                            <FileStack className="h-6 w-6" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">PDF site plan document</p>
                            <h3 className="mt-2 font-[Cormorant_Garamond,serif] text-2xl font-bold text-[#2C2C2C]">{document.label}</h3>
                            <p className="mt-3 text-sm leading-7 text-[#5D584F]">Open the uploaded PDF to review the project site orientation, masterplan, and supporting pages.</p>
                          </div>
                        </div>
                        <div className="mt-5 overflow-hidden rounded-[20px] border border-[#D4AF37]/20 bg-[#fffaf1]" data-testid={`listing-detail-siteplan-pdf-preview-${document.label.toLowerCase().replace(/\s+/g, "-")}`}>
                          <iframe className="h-[360px] w-full bg-white" src={`${document.url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`} title={`${document.label} preview`} />
                        </div>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <a className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16] transition hover:border-[#D4AF37]/70 hover:bg-[#f7ecd1]" data-testid={`listing-detail-siteplan-pdf-open-link-${document.label.toLowerCase().replace(/\s+/g, "-")}`} href={document.url} rel="noreferrer" target="_blank">
                            Open PDF <Download className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}

              {towerViewItems.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-tower-view-section">
                  <p className="subtle-label">Tower View</p>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {towerViewItems.map((item) => (
                      <button className="signature-greige-soft rounded-[22px] border border-[#D4AF37]/30 px-4 py-5 text-left transition hover:border-[#D4AF37]/70 hover:bg-[#F1E5D1]" data-testid={`listing-detail-tower-card-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.label} onClick={() => setLightboxImage({ src: item.image_url, alt: `${listing.title} ${item.label}`, variant: "plan" })} type="button">
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8b6d16]">{item.label}</p>
                        <p className="mt-3 text-sm leading-7 text-[#5D584F]">Click to inspect the corresponding floor plan or stack layout.</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {!floorplanImages.length && !siteplanImages.length && !towerViewItems.length && !floorplanDocuments.length && !siteplanDocuments.length ? (
                <div className="agent-paper-section signature-greige-shell rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" data-testid="listing-detail-floorplan-placeholder-section">
                  <p className="subtle-label">Floor Plans</p>
                  <div className="mt-6 signature-greige-soft rounded-[26px] border border-dashed border-[#D4AF37]/30 p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Floor-plan pack not published yet</p>
                    <p className="mt-3 text-sm leading-8 text-[#5D584F]">This listing does not currently include published floor plans or tower-stack visuals. The section remains in place so the page hierarchy stays consistent across the portfolio.</p>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="agent-paper-section signature-greige-shell scroll-mt-36 rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" id="gallery" data-testid="listing-detail-gallery-section">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-[#D4AF37]"><Camera className="h-5 w-5" /><p className={sectionEyebrowClass}>Gallery</p></div>
                <ImageMoodToggle mood={imageMood} onChange={setImageMood} testIdPrefix="listing-detail-gallery-mood" />
              </div>
              {galleryImages.length ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {galleryImages.map((image, index) => (
                    <button className="overflow-hidden rounded-[26px] border border-[#D4AF37]/20 bg-white" data-testid={`listing-detail-gallery-image-button-${index + 1}`} key={image} onClick={() => setLightboxImage({ src: image, alt: `${listing.title} gallery ${index + 1}`, variant: "photo" })} type="button">
                      <PropertyMoodImage alt={`${listing.title} gallery ${index + 1}`} dataTestId={`listing-detail-gallery-image-${index + 1}`} imageClassName="aspect-[1.2/1] w-full object-cover" mood={imageMood} src={image} />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-6 signature-greige-soft rounded-[26px] border border-dashed border-[#D4AF37]/30 p-6" data-testid="listing-detail-gallery-placeholder">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B6E3D]">Gallery refresh pending</p>
                  <p className="mt-3 text-sm leading-8 text-[#5D584F]">This listing does not yet have a synced gallery set. The section remains visible so the layout stays stable while media is being refreshed.</p>
                </div>
              )}
            </section>

            <section className="agent-paper-section signature-greige-shell scroll-mt-36 rounded-[30px] p-6 shadow-[0_18px_60px_rgba(0,25,53,0.06)] md:p-8" id="faq" data-testid="listing-detail-faq-section">
              <p className={sectionEyebrowClass}>FAQs</p>
              <Accordion className="mt-6" collapsible type="single">
                {faqItems.map((item, index) => (
                  <AccordionItem key={item.question} value={`faq-${index}`}>
                    <AccordionTrigger data-testid={`listing-detail-faq-trigger-${index + 1}`}>{item.question}</AccordionTrigger>
                    <AccordionContent data-testid={`listing-detail-faq-content-${index + 1}`}>{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          <aside className="hidden xl:block">
            <div className="signature-greige-shell sticky top-36 rounded-[34px] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.12)]" data-testid="listing-detail-contact-sidebar">
              <div className="flex items-center gap-4 rounded-[24px] border border-[#D4AF37]/25 bg-white p-5" data-testid="listing-detail-contact-brand-card">
                <div className="text-center">
                <img alt={branding.name} className="h-20 w-20 rounded-full object-cover ring-2 ring-[#D4AF37]/35" data-testid="listing-detail-contact-brand-logo" decoding="async" loading="lazy" src={branding.profile_photo_url || branding.team_logo_url || SITE_LOGO_URL} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">{sidebarEyebrow}</p>
                  <p className="mt-2 text-2xl font-semibold text-[#2C2C2C]">{branding.name}</p>
                  {branding.cea_registration_number ? <p className="mt-2 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">CEA {branding.cea_registration_number}</p> : null}
                </div>
              </div>
              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Contact the Team</p>
                <h3 className="mt-2 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#2C2C2C]">{sidebarHeading}</h3>
                <p className="mt-3 text-sm leading-7 text-[#5D584F]">{sidebarCopy}</p>
              </div>
              <div className="mt-6 grid gap-3">
                <a className="gold-button !w-full" data-testid="listing-detail-sidebar-whatsapp-button" href={listingWhatsAppHref} onClick={() => void trackLeadIntent({ premiumLead, eventType: "whatsapp_enquire_click", listing, metadata: { source: "listing_detail_sidebar_whatsapp" } })} rel="noreferrer" target="_blank"><MessageCircle className="mr-2 h-4 w-4" /> {whatsappButtonLabel}</a>
                <button className="outline-button !w-full" data-testid="listing-detail-brochure-button" onClick={() => setOpenModal(true)} type="button"><Download className="mr-2 h-4 w-4" /> {projectPackLabel}</button>
                <Link className="outline-button !w-full" data-testid="listing-detail-back-link" to={buildBrandedPath("/listings")}>Back to Listings</Link>
              </div>
              {isAgentPath ? <div className="mt-6">
                <AgentTrustPanel
                  branding={branding}
                  buildBrandedPath={buildBrandedPath}
                  compact
                  lastUpdatedAt={listing.updated_at}
                  listingCount={1}
                  onExploreNow={handleFindSimilarUnits}
                  testIdPrefix="listing-detail-agent-trust"
                  variant="signature-advisor"
                />
              </div> : null}
            </div>
          </aside>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#D4AF37]/25 bg-[#F9F8F6]/92 px-4 py-4 shadow-[0_-18px_40px_rgba(0,0,0,0.12)] backdrop-blur-xl md:hidden" data-testid="listing-detail-mobile-contact-bar">
        <div className="grid gap-3">
          <div className="flex items-center gap-3">
              <img alt={branding.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/35" data-testid="listing-detail-mobile-profile-photo" decoding="async" loading="lazy" src={branding.profile_photo_url || branding.team_logo_url || SITE_LOGO_URL} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#2C2C2C]">{branding.name}</p>
              <p className="truncate text-[11px] uppercase tracking-[0.16em] text-[#8B6E3D]">{branding.cea_registration_number ? `CEA ${branding.cea_registration_number}` : "Property guidance"}</p>
            </div>
          </div>
          <a className="gold-button !w-full !justify-center !py-3" data-testid="listing-detail-mobile-whatsapp-button" href={listingWhatsAppHref} onClick={() => void trackLeadIntent({ premiumLead, eventType: "whatsapp_enquire_click", listing, metadata: { source: "listing_detail_mobile_whatsapp" } })} rel="noreferrer" target="_blank">
            <MessageCircle className="mr-2 h-4 w-4" /> {whatsappButtonLabel}
          </a>
          <Link className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8B6E3D]" data-testid="listing-detail-mobile-contact-link" to={leadFormPath}>
            {mobileContactLinkLabel}
          </Link>
        </div>
      </div>

      {comparisonUnits.length ? <ListingCompareSummaryBar onClear={() => setSelectedComparisons([])} onRemove={toggleComparison} onReview={reviewComparison} units={comparisonUnits} /> : null}

      {lightboxImage ? (
        <button className="fixed inset-0 z-[70] flex items-center justify-center bg-[#001935]/80 p-6 backdrop-blur-sm" data-testid="listing-detail-image-lightbox" onClick={() => setLightboxImage(null)} type="button">
          {lightboxImage.variant === "photo" ? (
            <PropertyMoodImage alt={lightboxImage.alt} dataTestId="listing-detail-image-lightbox-photo" imageClassName="max-h-[88vh] max-w-[92vw] object-contain" mood={imageMood} src={lightboxImage.src} wrapperClassName="max-h-[88vh] max-w-[92vw] rounded-[28px] border border-white/10 shadow-[0_30px_90px_rgba(0,0,0,0.35)]" />
          ) : (
                <img alt={lightboxImage.alt} className="max-h-[88vh] max-w-[92vw] rounded-[28px] border border-white/10 object-contain shadow-[0_30px_90px_rgba(0,0,0,0.35)]" decoding="async" loading="lazy" src={lightboxImage.src} />
          )}
        </button>
      ) : null}

      <BrochureModal listing={listing} onClose={() => setOpenModal(false)} open={openModal} />
      <InvestmentForecastReportModal listing={listing} onClose={() => setOpenForecastModal(false)} onUnlocked={setPremiumLead} open={openForecastModal} />
    </div>
  );
};

export const ListingDetailRoute = () => {
  const { listingId } = useParams();
  const { buildBrandedPath } = useSiteBranding();

  return (
    <RouteErrorBoundary
      fallback={<ListingRouteErrorState homePath={buildBrandedPath("/")} listingsPath={buildBrandedPath("/listings")} />}
      resetKey={listingId}
    >
      <ListingDetailPage />
    </RouteErrorBoundary>
  );
};